import React, { useState } from 'react';
import VerificationPageLayout from '../../components/Layout/VerificationPageLayout';
import SchemaForm, { FormField } from '../../components/Forms/SchemaForm';
import VerificationResultDisplay from '../../components/Verification/VerificationResultDisplay';
import {
    businessOnlineEnquiryService,
    BusinessOnlineEnquiryPayload,
    BusinessOnlineEnquiryResultPayload,
    BusinessMatchItem,
} from '../../services/verificationSystemServices/businessOnlineEnquiryService';
import {
    Box,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import ActionButton from '../../components/Button/ActionButton';

const BusinessOnlineEnquiry: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [matches, setMatches] = useState<BusinessMatchItem[]>([]);
    const [selectedTrace, setSelectedTrace] = useState<any | null>(null);
    const [reportData, setReportData] = useState<any | null>(null);
    const [searchParams, setSearchParams] = useState<BusinessOnlineEnquiryPayload | null>(null);

    const schema: FormField[] = [
        { name: 'reg1', label: 'Reg1', type: 'text', required: false, gridProps: { xs: 12, md: 4 } },
        { name: 'reg2', label: 'Reg2', type: 'text', required: false, gridProps: { xs: 12, md: 4 } },
        { name: 'reg3', label: 'Reg3', type: 'text', required: false, gridProps: { xs: 12, md: 4 } },
        { name: 'businessName', label: 'Business Name', type: 'text', required: false, gridProps: { xs: 12, md: 6 } },
        { name: 'vatNo', label: 'Vat No', type: 'text', required: false, gridProps: { xs: 12, md: 6 } },
        { name: 'solePropIdNo', label: 'SolePropIDNo', type: 'text', required: false, gridProps: { xs: 12, md: 6 }, maxLength: 13 },
        { name: 'yourReference', label: 'Your Reference', type: 'text', required: false, gridProps: { xs: 12, md: 6 } },
        { name: 'voucherCode', label: 'Voucher Code', type: 'text', required: false, gridProps: { xs: 12, md: 6 } },
    ];

    const handleSearchSubmit = async (data: any) => {
        setLoading(true);
        setError(null);
        setMatches([]);
        setSelectedTrace(null);
        setReportData(null);
        setSearchParams(data as BusinessOnlineEnquiryPayload);

        try {
            const response = await businessOnlineEnquiryService.getMatch(data as BusinessOnlineEnquiryPayload);

            if (response.success && response.matches.length > 0) {
                setMatches(response.matches);
            } else if (response.success && response.matches.length === 0) {
                setError('No matches found for the provided search parameters.');
            } else {
                setError(response.message || 'Failed to find matching businesses.');
            }
        } catch (err: any) {
            setError(err?.response?.data?.detail || err.message || 'An unexpected network error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const handleFetchReport = async (match: BusinessMatchItem) => {
        setLoading(true);
        setError(null);
        setSelectedTrace(match);

        const payload: BusinessOnlineEnquiryResultPayload = {
            enquiryId: parseInt(match.enquiry_id, 10),
            enquiryResultId: parseInt(match.enquiry_result_id, 10),
            yourReference: searchParams?.yourReference || '',
            voucherCode: searchParams?.voucherCode || '',
        };

        try {
            const response = await businessOnlineEnquiryService.getResult(payload);
            if (response.success && response.data?.raw_data) {
                setReportData(response.data.raw_data);
            } else {
                setError(response.message || 'Failed to fetch final Business report.');
            }
        } catch (err: any) {
            setError(err?.response?.data?.detail || err.message || 'An error occurred fetching the report.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <VerificationPageLayout title="Business Online Enquiry">

            <Box mb={4}>
                <SchemaForm
                    schema={schema}
                    onSubmit={handleSearchSubmit}
                    defaultValues={{}}
                    submitLabel={loading && !selectedTrace ? 'Searching...' : 'Search'}
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
            )}

            {loading && !selectedTrace && matches.length === 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 2 }}>
                    <CircularProgress sx={{ color: '#2D4677' }} />
                </Box>
            )}

            {matches.length > 0 && !reportData && (
                <Box mb={4}>
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        mb={2}
                    >
                        Select a candidate from the table below to extract their full commercial trace report.
                    </Typography>

                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: '1px solid #dce6f5',
                            borderRadius: '12px',
                            overflow: 'hidden',
                        }}
                    >
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#EFF3FB' }}>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            color: '#132F5F',
                                            fontSize: '0.78rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        Registration No
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            color: '#132F5F',
                                            fontSize: '0.78rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        Business Name
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 700,
                                            color: '#132F5F',
                                            fontSize: '0.78rem',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                        }}
                                    >
                                        Commercial ID
                                    </TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {matches.map((match, idx) => (
                                    <TableRow
                                        key={idx}
                                        hover
                                        sx={{ '&:last-child td': { borderBottom: 'none' } }}
                                    >
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>
                                            {match.registration_no || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a2e' }}>
                                            {match.business_name || '—'}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '0.85rem', color: '#4a5568' }}>
                                            {match.commercial_id || '—'}
                                        </TableCell>
                                        <TableCell align="right">
                                            <ActionButton
                                                size="small"
                                                disabled={loading}
                                                onClick={() => handleFetchReport(match)}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    py: 0.75,
                                                    px: 2,
                                                }}
                                            >
                                                {loading && selectedTrace?.enquiry_result_id === match.enquiry_result_id
                                                    ? 'Fetching...'
                                                    : 'Extract Result'}
                                            </ActionButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {reportData && (
                <>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={() => { setReportData(null); setSelectedTrace(null); }}
                        sx={{
                            mb: 2,
                            borderColor: '#2D4677',
                            color: '#2D4677',
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { borderColor: '#1a365d', backgroundColor: 'rgba(45,70,119,0.04)' },
                        }}
                    >
                        ← Back to Matches
                    </Button>
                    <VerificationResultDisplay
                        title="Business Online Enquiry"
                        success
                        message="Business report retrieved successfully."
                        data={reportData}
                    />
                </>
            )}

        </VerificationPageLayout>
    );
};

export default BusinessOnlineEnquiry;
